import { useGetServicesPaginated } from "@/hooks/services/use-services";
import {
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  MRT_ToggleFiltersButton,
} from "material-react-table";
import { useMemo } from "react";

import { APIPaginatedTable } from "@/components/common/api-paginated-table";
import type { APIPaginatedTableState } from "@/components/common/api-paginated-table/states";
import type { Service } from "@/types/services/api-types/service-types";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

const createServicesFilters = (columnFilters: MRT_ColumnFiltersState) => {
  const filters: {
    name?: string;
    category_name?: string;
  } = {};

  const getTrimmedString = (value: unknown) =>
    typeof value === "string" ? value.trim() : "";

  columnFilters.forEach((filter) => {
    const colName = filter.id.split(".")[1] ?? filter.id;
    const trimmedValue = getTrimmedString(filter.value);
    if (trimmedValue === "") return;

    if (colName === "name") {
      filters.name = trimmedValue;
    }
    if (colName === "category_name") {
      filters.category_name = trimmedValue;
    }
  });

  return filters;
};

interface ServicesTableProps {
  onServiceClick: (service: Service) => void;
  onDeleteClick: (service: Service) => void;
}

const ServicesTable = ({
  onServiceClick,
  onDeleteClick,
}: ServicesTableProps) => {
  const columns = useMemo<MRT_ColumnDef<Service>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row, renderedCellValue }) => {
          const service = row.original;
          return (
            <button
              onClick={() => onServiceClick?.(service)}
              className="text-purple-500 hover:underline cursor-pointer"
            >
              {renderedCellValue}
            </button>
          );
        },
      },
      {
        accessorKey: "category_name",
        header: "Category",
      },
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => {
          const service = row.original;
          return (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onDeleteClick(service)}
            >
              <Trash2 />
            </Button>
          );
        },
      },
    ],
    [onServiceClick, onDeleteClick]
  );

  return (
    <>
      <APIPaginatedTable
        columns={columns}
        reactQuery={useGetServicesPaginated}
        reactQueryFilters={(state: APIPaginatedTableState) => {
          const filters = createServicesFilters(state.columnFilters);

          return {
            page: state.pagination.pageIndex,
            size: state.pagination.pageSize,
            ...filters,
          };
        }}
        onRenderToolbarInternalActions={({ table }) => (
          <>
            <MRT_ToggleFiltersButton table={table} />
          </>
        )}
        titleTopic="services"
        contentTopic="service"
      />
    </>
  );
};

export default ServicesTable;
